import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { SpinnerIcon } from '@/components/icons';
import { useAuth } from '@/features/auth/context/auth-context';

import { ROUTES } from './routes';

/**
 * Garde de route : réserve une branche du routeur aux utilisateurs connectés.
 *
 * ⚠️ Ce n'est **pas** une mesure de sécurité. Le code du navigateur est
 * modifiable par qui le souhaite : la seule protection réelle est le contrôle
 * effectué par l'API sur chaque requête. Cette garde sert l'expérience — éviter
 * d'afficher une coquille vide à un utilisateur non authentifié.
 *
 * L'état `loading` est déterminant : pendant l'échange du cookie de refresh au
 * démarrage, on ne sait pas encore si une session existe. Rediriger dès cet
 * instant déconnecterait visuellement l'utilisateur à chaque rafraîchissement
 * de page.
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="grid min-h-dvh place-items-center" role="status" aria-live="polite">
        <SpinnerIcon className="text-navy-300 text-3xl" />
        <span className="sr-only">Vérification de votre session…</span>
      </div>
    );
  }

  if (status === 'anonymous') {
    // `state.from` mémorise la destination voulue : après connexion, on y
    // renvoie l'utilisateur plutôt que de le déposer systématiquement sur le
    // tableau de bord. `replace` évite d'empiler la page protégée dans
    // l'historique, où le bouton « retour » la rejouerait en boucle.
    return <Navigate to={ROUTES.login} replace state={{ from: location.pathname }} />;
  }

  return children;
};
